import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    NotificationDetails,
    UserNotificationRequest,
    UserNotificationResultsDescription,
} from '@routes/sidebar/notifications/api/notifications.api.ts';
import {
    getUserNotifications,
    putApiMarkSeenNotification,
    putApiToggleNotificationRead,
    putApiToggleReadAllNotifications,
} from '@routes/sidebar/notifications/api/notifications.api.ts';
import type { CommonSearchRequest, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

interface NotificationsSlice {
    error: Record<string, any> | null;
    notificationsData: PaginatedResponse<NotificationDetails, UserNotificationResultsDescription>;
    seenNotifications: number[];
    readNotification: number;
    isReadNotificationSend: boolean;
    notificationRequest: UserNotificationRequest;
    suppressViewLoading: boolean;
    updatingReadStateId: number | null;
    scrollTopPosition: number | null;

    isGetNotificationsPending: boolean;
    isToggleReadNotificationPending: boolean;
    isMarkSeenNotificationsPending: boolean;
    isToggleReadAllNotificationsPending: boolean;
}

const INITIAL_NOTIFICATIONS_DATA: PaginatedResponse<NotificationDetails, UserNotificationResultsDescription> = {
    data: [],
    pagination: { maxPageSize: 0, pageNumber: 1, pageSize: 1, totalPages: 0, totalCount: 0 },
    resultsDescription: {
        totalNewNotifications: 0,
        assignedNew: 0,
        unreadNew: 0,
    },
};

const INITIAL_NOTIFICATION_REQUEST: CommonSearchRequest = {
    pageNumber: 1,
    pageSize: 10,
    filters: null,
    textSearch: '',
    sortOrder: 'descending',
    sortBy: 'createdAtUTC',
};

const initialState: NotificationsSlice = {
    error: null,
    notificationsData: INITIAL_NOTIFICATIONS_DATA,
    readNotification: -1,
    seenNotifications: [],
    isReadNotificationSend: false,
    notificationRequest: INITIAL_NOTIFICATION_REQUEST,
    suppressViewLoading: false,
    updatingReadStateId: null,
    scrollTopPosition: null,

    isGetNotificationsPending: true,
    isToggleReadNotificationPending: false,
    isMarkSeenNotificationsPending: false,
    isToggleReadAllNotificationsPending: false,
};

export const getNotifications = createAsyncThunk<
    PaginatedResponse<NotificationDetails, UserNotificationResultsDescription>,
    UserNotificationRequest,
    { rejectValue: any }
>('notifications/userNotifications', (payload, { rejectWithValue }) =>
    getUserNotifications(payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const toggleReadNotification = createAsyncThunk<void, number, { rejectValue: any }>(
    'notifications/toggleReadNotification',
    (id, { rejectWithValue }) =>
        putApiToggleNotificationRead(id)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const markSeenNotifications = createAsyncThunk<void, number[], { rejectValue: any }>(
    'notifications/markSeenNotifications',
    (ids, { rejectWithValue }) =>
        putApiMarkSeenNotification(ids)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const toggleReadAllNotifications = createAsyncThunk<void, boolean, { rejectValue: any }>(
    'notifications/toggleReadAllNotifications',
    (wasRead, { rejectWithValue }) =>
        putApiToggleReadAllNotifications(wasRead)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        updateNotificationRequest: (state, action: PayloadAction<UserNotificationRequest>) => {
            state.notificationRequest = action.payload;
        },
        setSuppressViewLoading: (state, action: PayloadAction<boolean>) => {
            state.suppressViewLoading = action.payload;
        },
        setScrollTopPosition(state, action: PayloadAction<number | null>) {
            state.scrollTopPosition = action.payload;
        },
        clearNotifications: () => ({ ...initialState }),
    },
    extraReducers: (builder) => {
        builder.addCase(getNotifications.pending, (state) => {
            state.error = null;
            state.isGetNotificationsPending = true;
        });
        builder.addCase(getNotifications.fulfilled, (state, action) => {
            state.notificationsData = action.payload;
            state.suppressViewLoading = false;
            state.isGetNotificationsPending = false;
        });
        builder.addCase(getNotifications.rejected, (state, action) => {
            state.error = extractError(action);
            state.suppressViewLoading = false;
            state.isGetNotificationsPending = false;
        });
        // Read
        builder.addCase(toggleReadNotification.pending, (state, action) => {
            state.error = null;
            state.suppressViewLoading = true;
            state.isReadNotificationSend = true;
            state.updatingReadStateId = action.meta.arg;
            state.isToggleReadNotificationPending = true;
        });
        builder.addCase(toggleReadNotification.fulfilled, (state, action) => {
            state.suppressViewLoading = false;
            state.readNotification = action.meta.arg;
            state.isReadNotificationSend = false;
            state.updatingReadStateId = null;
            state.isToggleReadNotificationPending = false;
        });
        builder.addCase(toggleReadNotification.rejected, (state, action) => {
            state.suppressViewLoading = false;
            state.isReadNotificationSend = false;
            state.error = extractError(action);
            state.updatingReadStateId = null;
            state.isToggleReadNotificationPending = false;
        });

        builder.addCase(markSeenNotifications.pending, (state) => {
            state.error = null;
            state.isMarkSeenNotificationsPending = true;
        });
        builder.addCase(markSeenNotifications.fulfilled, (state, action) => {
            state.seenNotifications = action.meta.arg;
            state.isMarkSeenNotificationsPending = false;
        });
        builder.addCase(markSeenNotifications.rejected, (state, action) => {
            state.error = extractError(action);
            state.isMarkSeenNotificationsPending = false;
        });

        builder.addCase(toggleReadAllNotifications.pending, (state) => {
            state.error = null;
            state.isToggleReadAllNotificationsPending = true;
        });
        builder.addCase(toggleReadAllNotifications.fulfilled, (state) => {
            state.isToggleReadAllNotificationsPending = false;
        });
        builder.addCase(toggleReadAllNotifications.rejected, (state, action) => {
            state.error = extractError(action);
            state.isToggleReadAllNotificationsPending = false;
        });
    },
});

export const { updateNotificationRequest, setSuppressViewLoading, setScrollTopPosition, clearNotifications } =
    notificationsSlice.actions;

export default notificationsSlice.reducer;
